import { Dispute } from 'APIs/disputes/disputes.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { FormItem, Label } from 'Constants/styles';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { FormCheckbox, FormControl, FormWrapper, VInput } from './styles';
import { SetDisputeOutcomeFormProps } from './types';

type Props = {
  setDisputeOutcomeFormProps: SetDisputeOutcomeFormProps | null;
  updateDisputeHendler: (formData: SetDisputeOutcomeFormProps) => void;
  modalErrorMsg: string;
  isModalLoading: boolean;
  setModalDispute: (modalDispute: Dispute | null) => void;
};

const SetDisputeOutcomeModal = ({
  setDisputeOutcomeFormProps,
  updateDisputeHendler,
  modalErrorMsg,
  isModalLoading,
  setModalDispute,
}: Props): ReactElement => {
  const [hideError, setHideError] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    disputeOutcome: Yup.string()
      .oneOf(['won', 'lost'], 'Dispute outcome should be either won or lost')
      .required('Dispute outcome is required'),
    state: Yup.string().trim().required('State is required'),
  });

  const defaultValues: SetDisputeOutcomeFormProps = {
    disputeId: '',
    disputeOutcome: 'pending',
    state: '',
    reverseDisputeFees: false,
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SetDisputeOutcomeFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: setDisputeOutcomeFormProps ?? defaultValues,
  });

  const onSubmit = useCallback(
    async (formData) => {
      updateDisputeHendler(formData);
    },
    [updateDisputeHendler],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = () => {
    setModalDispute(null);
  };

  useEffect(() => {
    if (modalErrorMsg) {
      setHideError(false);
    }
  }, [modalErrorMsg]);

  const disputeOutcome = watch('disputeOutcome');

  return (
    <Modal
      title={'Update dispute outcome'}
      cancelBtnText="Cancel"
      confirmBtnText={'Update'}
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isModalLoading}
      isLoading={isModalLoading}
      large={false}
      testId={'set-dispute-outcome-modal'}
    >
      {modalErrorMsg.length > 0 && !hideError && (
        <Message
          className="mt-2 mb-0"
          success={false}
          description={modalErrorMsg}
          handleClose={(e) => {
            e.preventDefault();
            setHideError(true);
          }}
        />
      )}
      <FormWrapper>
        <FormItem>
          <Label>Dispute outcome</Label>
          <FormControl {...register('disputeOutcome')}>
            {['pending', 'won', 'lost'].map((outcome) => (
              <option key={outcome} value={outcome}>
                {outcome.charAt(0).toUpperCase() + outcome.slice(1)}
              </option>
            ))}
          </FormControl>
          {errors.disputeOutcome && <span className="error-msg">{errors.disputeOutcome.message}</span>}
        </FormItem>
        <FormItem>
          <Label>State</Label>
          <VInput {...register('state')} isValid={!errors.state} />
          {errors.state && <span className="error-msg">{errors.state.message}</span>}
        </FormItem>
        {disputeOutcome === 'won' && (
          <FormItem>
            <Label>Reverse dispute fees</Label>
            <FormCheckbox type="checkbox" {...register('reverseDisputeFees')} />
            {errors.reverseDisputeFees && <span className="error-msg">{errors.reverseDisputeFees.message}</span>}
          </FormItem>
        )}
      </FormWrapper>
    </Modal>
  );
};

export default SetDisputeOutcomeModal;
